import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import type { AppRoute } from "@/lib/routes";

type Feature = Exclude<NonNullable<AppRoute["feature"]>, "mcp">;

/** What each deployment feature is called, and how it is turned on. */
const OFF: Record<Feature, { title: string; message: string; fixedMessage: string }> = {
  monitor: {
    title: "skyMonitor is off",
    message:
      "skyMonitor is off for this deployment. A platform admin turns it on in Platform settings, Feature flags.",
    // #2501: the deployment policy fixes it, so Feature flags has no switch for it.
    fixedMessage: "skyMonitor is off for this deployment. A change goes through Skypoint.",
  },
};

/**
 * A route whose deployment feature is off (`featureAllows`, `GET /me` `features`). The route has
 * no nav entry; an address or a link that reaches it says the feature is off and who turns it on,
 * under the route's own title, rather than "That page does not exist" under that title.
 * skyMonitor only: AI apps do not exist at all while off (#1812, owner 2026-10-22,
 * `featureHiddenWhenOff`), so their addresses read "That page does not exist".
 */
export function FeatureOffNotice({ feature }: { feature: Feature }) {
  const fixed = useTenantInstance().me?.features?.fixedByDeployment?.includes(feature) === true;
  const { title, message, fixedMessage } = OFF[feature];
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title={title}
      message={fixed ? fixedMessage : message}
      testId={`${feature}-off`}
    />
  );
}
